import { useCallback, useMemo } from 'react';
import { useApolloClient } from '@apollo/client';

import {
  useDisableOrphanPeriodicTaskActionMutation,
  useScheduledTasksListQuery,
} from 'core/graphql';

import {
  runtimeCrashResult,
  IOrphanTask,
  IRuntimeActionResult,
  IScheduledEntry,
  toRuntimeActionResult,
  toOrphanTask,
  toScheduledEntry,
} from './runtimeSettingsModel';
import { RUNTIME_FETCH } from './useRuntimeSettings';

/**
 * ТЗ 0093 этап 2б — периодические задачи: записи `beat_schedule` (ключи,
 * действует ли запись, строка планировщика, последний прогон) и беспризорные
 * строки `PeriodicTask`, которых в расписании нет.
 *
 * У записей и строк нет `id` — они живут внутри ответа `scheduledTasks`,
 * поэтому после «Выключить» список перечитывается этим же хуком, а не
 * `refetchQueries` по имени. Изменение ключа в соседней вкладке выбрасывает
 * `scheduledTasks` из кэша (`evictRuntimeDerived`) — открытие вкладки
 * запросит свежий список (открытая — сразу: после вытеснения в кэше пусто,
 * и `cache-first` идёт в сеть).
 */
export const useScheduledTasks = () => {
  const client = useApolloClient();
  const query = useScheduledTasksListQuery({ ...RUNTIME_FETCH });
  const [disableMut] = useDisableOrphanPeriodicTaskActionMutation();

  const entries = useMemo<IScheduledEntry[]>(
    () => (query.data?.scheduledTasks.entries ?? []).map(toScheduledEntry),
    [query.data],
  );
  const orphans = useMemo<IOrphanTask[]>(
    () => (query.data?.scheduledTasks.orphans ?? []).map(toOrphanTask),
    [query.data],
  );

  const { refetch: refetchQuery } = query;
  const refetch = useCallback(() => {
    refetchQuery().catch(() => undefined);
  }, [refetchQuery]);

  const disableOrphan = useCallback(
    async (name: string, reason: string): Promise<IRuntimeActionResult> => {
      try {
        const { data } = await disableMut({
          variables: { name, reason: reason.trim() || null },
        });
        const result = toRuntimeActionResult(data?.disableOrphanPeriodicTask);
        if (result.ok) {
          client.cache.evict({
            id: 'ROOT_QUERY',
            fieldName: 'runtimeSettingChanges',
          });
          await refetchQuery().catch(() => undefined);
        }
        return result;
      } catch (caught) {
        return runtimeCrashResult(caught);
      }
    },
    [client, disableMut, refetchQuery],
  );

  return {
    entries,
    orphans,
    loading: query.loading && !query.data,
    error: Boolean(query.error) && !query.data,
    refetch,
    disableOrphan,
  };
};
